import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { getTourPreview } from "../../services/admin-tours";
import { getMyTours, recordTourProgress } from "../../services/tours";
import type { RecordTourProgressRequest, TourOutcome } from "../../types/tours";
import { COACH_PRESS_ANCHORS, WEB_TOUR_ANCHORS } from "./tourAnchors";
import {
  TourContext,
  type PageRegistration,
  type ReplayRequest,
  type TourHandOff,
  type TourContextValue,
} from "./tourContext";
import {
  continuationOf,
  dueRun,
  findProgress,
  permissionsForTours,
  renderableSteps,
  replayRun,
  resolveTourKeys,
  withoutAbsentRecordSteps,
  type RenderContext,
  type TourKeyOverrides,
  type TourRun,
} from "./tourEngine";
import {
  closeAnchorsOpenedByRun,
  closeCommitModal,
  isCommitInFlight,
  closePopupsLeftBehind,
  findAnchorElement,
  isAnchorFolded,
  openerPressPlan,
  openPopupOpenerKeys,
  pressOpenersInOrder,
  scheduleFrame,
  shownStepIndex,
} from "./tourDom";
import type { TourState } from "./tourReducer";
import { TourOverlay } from "./TourOverlay";
import { TourPickMode } from "./TourPickMode";
import { TourPreviewNotice } from "./TourPreviewNotice";
import { createTourProgressGuard, type TourProgressGuard } from "./tourProgressGuard";
import { initialTourState, runAfterEnd, runFollowsEnd, tourReducer } from "./tourReducer";

const sendProgress = (
  guard: TourProgressGuard,
  tourKey: string,
  body: RecordTourProgressRequest
) =>
  recordTourProgress(tourKey, body)
    .then(() => guard.removePending(tourKey, body.seenAt))
    .catch((error) => {
      // Kept as a pending write and retried on the next load for this user.
      console.warn(`Could not record progress for tour ${tourKey}; will retry:`, error);
    });

/** `run` without the steps whose record is not on the page, or whose control is folded away (`withoutAbsentRecordSteps`). */
const withoutAbsentSteps = (run: TourRun): TourRun =>
  withoutAbsentRecordSteps(run, (anchorId) => findAnchorElement(anchorId) !== null, isAnchorFolded);

/**
 * Whether every step of `run` is rendered as a centered card: a data tour whose page has lost the
 * rows it points at.
 */
const anchorSuppressed = (state: TourState, run: TourRun): boolean =>
  run.tour.kind === "Data" && state.pages[run.tour.feature]?.hasData === false;

interface TourProviderProps {
  children: ReactNode;
}

/**
 * Loads guided tours for the signed-in user and runs at most one at a time.
 *
 * Mounted inside `AuthProvider` and the router. Tours are optional: any failure to load them
 * (an API without tours, a network error, a paused organization) disables them silently.
 */
export const TourProvider = ({ children }: TourProviderProps) => {
  const { user, effectiveOrganizationId, isSystemAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(tourReducer, initialTourState);

  const userId = user?.profile?.id ?? null;
  const scope = userId && effectiveOrganizationId ? `${userId}|${effectiveOrganizationId}` : null;
  const role = user?.profile?.role;
  const permissions = user?.profile?.permissions;

  const renderContext = useMemo<RenderContext>(
    () => ({ knownAnchors: WEB_TOUR_ANCHORS, permissions: permissionsForTours(role, permissions) }),
    [role, permissions]
  );

  const guard = useMemo(
    () =>
      userId && effectiveOrganizationId
        ? createTourProgressGuard(userId, effectiveOrganizationId)
        : null,
    [userId, effectiveOrganizationId]
  );

  /**
   * Cancels an end that is waiting for a frame (see `anchorUsed`). Unmounting is leaving the page
   * mid-tour, which closes the run and records nothing, so the pending end goes with it.
   */
  const cancelPendingEnd = useRef<(() => void) | null>(null);
  useEffect(
    () => () => {
      cancelPendingEnd.current?.();
      cancelPendingEnd.current = null;
    },
    []
  );

  // Latest state for event handlers, so the callbacks below can stay referentially stable.
  const stateRef = useRef(state);
  const renderContextRef = useRef(renderContext);
  useLayoutEffect(() => {
    stateRef.current = state;
    renderContextRef.current = renderContext;
  });

  /**
   * For each step of the active run the coach has moved on from: the steps whose popups were open
   * while it was on screen (`openPopupOpenerKeys`). Back reads it to put the page back the way the
   * step it lands on was shown. Per run: a new run, or the same tour started again, starts empty.
   */
  const openersByStepRef = useRef<{ run: TourRun | null; byStep: Map<string, string[]> }>({
    run: null,
    byStep: new Map(),
  });
  /**
   * Steps of the active run that moving forward passed over as orphaned (`isOrphanedSubStep`), so
   * Back passes over them too instead of landing on a card about a popup that cannot open. Only
   * these: a step that was shown and whose popup has since closed is one Back restores. Per run.
   */
  const skippedRef = useRef<{ run: TourRun | null; keys: Set<string> }>({
    run: null,
    keys: new Set(),
  });
  const noteSkipped = useCallback((run: TourRun, fromIndex: number, toIndex: number) => {
    if (skippedRef.current.run !== run) skippedRef.current = { run, keys: new Set() };
    const { keys } = skippedRef.current;
    // What this move passed over replaces what an earlier pass over the same steps noted: a step
    // skipped then (its popup could not open) and shown now (Back re-opened it) is no longer one
    // Back should pass over.
    for (let index = fromIndex + 1; index <= toIndex; index += 1) {
      const key = run.steps[index]?.key;
      if (key) keys.delete(key);
    }
    for (let index = fromIndex + 1; index < toIndex; index += 1) {
      const key = run.steps[index]?.key;
      if (key) keys.add(key);
    }
  }, []);
  /**
   * Cancels a Back that is still re-opening popups over several frames. Null once it has finished,
   * so it doubles as "a restore is running".
   */
  const cancelRestoreRef = useRef<(() => void) | null>(null);
  /** Stops a running restore. True when one was still running, so the page is half restored. */
  const cancelRestore = useCallback((): boolean => {
    const cancel = cancelRestoreRef.current;
    cancelRestoreRef.current = null;
    cancel?.();
    return cancel !== null;
  }, []);

  const activeRunForRecord = state.active?.run ?? null;
  useLayoutEffect(() => {
    if (openersByStepRef.current.run === activeRunForRecord) return;
    openersByStepRef.current = { run: activeRunForRecord, byStep: new Map() };
    cancelRestore();
  }, [activeRunForRecord, cancelRestore]);
  useEffect(
    () => () => {
      cancelRestore();
    },
    [cancelRestore]
  );

  /** Records which popups the current step had open, as the coach leaves it going forward. */
  const recordOpenPopups = useCallback(() => {
    const active = stateRef.current.active;
    const step = active?.run.steps[active.stepIndex];
    if (!active || !step) return;
    if (openersByStepRef.current.run !== active.run) {
      openersByStepRef.current = { run: active.run, byStep: new Map() };
    }
    openersByStepRef.current.byStep.set(
      step.key,
      openPopupOpenerKeys(active.run.steps, active.satisfiedStepKeys, step.key)
    );
  }, []);

  /*
   * Closes what the active run opened, while the overlay is still mounted and the steps and their
   * record are still to hand. `closeAnchorsOpenedByRun` presses only what this run itself opened
   * and what is still open.
   *
   * Every way a run leaves the screen has to come through here, not only `endRun`: a run that goes
   * without it leaves the popup it asked the coach to open sitting behind whatever runs next,
   * inerted and with nothing to close it.
   *
   * `extraSatisfiedKey` is a press that has just landed and is not in the run's record yet. The
   * record only refreshes on the re-render after the dispatch that adds it, so a press made on the
   * run's *final* step — the press that ends the run — would otherwise be invisible to a tidy-up
   * asked for in the same handler.
   */
  const closeWhatTheRunOpened = useCallback(
    (extraSatisfiedKey: string | null = null, closeModals = true) => {
      const active = stateRef.current.active;
      if (!active) return;
      const satisfied =
        extraSatisfiedKey === null || active.satisfiedStepKeys.includes(extraSatisfiedKey)
          ? active.satisfiedStepKeys
          : [...active.satisfiedStepKeys, extraSatisfiedKey];
      closeAnchorsOpenedByRun(active.run.steps, satisfied, document, { closeModals });
    },
    []
  );

  // Load once per user and effective organization. Switching either, or logging out, drops the
  // running tour without recording it.
  useEffect(() => {
    if (!guard || !scope) {
      dispatch({ type: "reset" });
      return;
    }
    let cancelled = false;
    dispatch({ type: "loadStart", scope });

    getMyTours()
      .then((response) => {
        if (cancelled) return;
        // After the load rather than before it: only the tours say when each was last re-shown, and
        // a retried write from before a re-show would mark the tour seen again on the server.
        guard.pendingWrites(response.tours).forEach((write) => {
          void sendProgress(guard, write.tourKey, write.body);
        });
        dispatch({
          type: "loadSucceeded",
          scope,
          tours: response.tours,
          progress: guard.mergeInto(response.progress, response.tours),
        });
      })
      .catch((error) => {
        if (cancelled) return;
        console.warn("Guided tours are unavailable:", error);
        dispatch({ type: "loadFailed", scope });
      });

    return () => {
      cancelled = true;
    };
  }, [guard, scope]);

  // Another tab finished or skipped a tour: close it here too, and never show it again.
  useEffect(() => {
    if (!guard) return;
    return guard.subscribe((tourKey) => {
      const local = guard
        .mergeInto([], stateRef.current.tours)
        .find((entry) => entry.tourKey === tourKey);
      dispatch({ type: "doneElsewhere", tourKey, seenStepKeys: local?.seenStepKeys ?? [] });
    });
  }, [guard]);

  /**
   * A replay chosen from the Guided tours list, waiting for its page's `useTour` to run it.
   *
   * Its lifetime is the visit to `request.path`. It is dropped when the hook consumes it, when the
   * pathname changes to anything other than that path, and when the user or organization changes.
   * So a request whose page never became ready cannot fire later on some unrelated visit. The
   * navigation that carries the user to the page does not drop it: that change lands on `path`.
   */
  const [pendingReplay, setPendingReplay] = useState<ReplayRequest | null>(null);

  /**
   * The tour last finished on the current pathname, for `handOff`: "just finished" means finished
   * on this visit, so it is dropped on any pathname change and when the user or organization
   * changes. Skipping does not set it, since a coach who skipped has said no to the next tour too.
   */
  const finishedHereRef = useRef<string | null>(null);

  useEffect(() => {
    setPendingReplay(null);
    finishedHereRef.current = null;
  }, [scope]);

  // Leaving the page mid-tour closes it and records nothing. Compared on pathname, not the
  // location key: a page replacing its own search params is not leaving.
  const pathnameRef = useRef(location.pathname);
  useEffect(() => {
    if (pathnameRef.current === location.pathname) return;
    const pathname = location.pathname;
    pathnameRef.current = pathname;
    finishedHereRef.current = null;
    dispatch({ type: "close" });
    setPendingReplay((current) => (current && current.path !== pathname ? null : current));
  }, [location.pathname]);

  /**
   * The page a preview was run on. Auto-start stays held there until the admin leaves it: dropping
   * the parameter is a navigation, and without the hold the published version, if due, would start
   * straight after the draft and be recorded as seen.
   */
  const [previewedPath, setPreviewedPath] = useState<string | null>(null);
  const previewVisit = previewedPath !== null && previewedPath === location.pathname;

  /*
   * The location key settles a frame after it changes, so auto-start never measures the old page.
   *
   * Held in a ref and handed out through `whenRouteSettled`, never as state on the context value:
   * search params are navigation too, so a filter or a page change would otherwise re-render every
   * consumer twice. A layout effect, so it is marked unsettled before any passive effect in the
   * same commit (`useTour`'s auto-start) can ask.
   */
  const routeRef = useRef({ key: location.key, settled: true });
  const settleListeners = useRef(new Set<() => void>());
  useLayoutEffect(() => {
    // Keyed on "settled" as well as the key, so StrictMode's re-run after a cancelled frame
    // schedules the frame again instead of leaving the route unsettled for good.
    if (routeRef.current.settled && routeRef.current.key === location.key) return;
    const key = location.key;
    routeRef.current = { key, settled: false };
    return scheduleFrame(() => {
      routeRef.current = { key, settled: true };
      const listeners = Array.from(settleListeners.current);
      settleListeners.current.clear();
      listeners.forEach((listener) => listener());
    });
  }, [location.key]);

  const whenRouteSettled = useCallback((callback: () => void) => {
    if (routeRef.current.settled) {
      callback();
      return () => {};
    }
    const listeners = settleListeners.current;
    listeners.add(callback);
    return () => {
      listeners.delete(callback);
    };
  }, []);

  /**
   * `?tourPreview=<key>`: a system admin looking at an unpublished draft.
   *
   * It runs as a replay, so it ignores seen state and records nothing, locally or on the server,
   * and the bubble carries a Preview marker. Anyone else gets the parameter ignored outright - the
   * endpoint is admin-only, and asking for it as a coach would only produce a console error.
   */
  const adminParams = useMemo(
    () => (isSystemAdmin ? new URLSearchParams(location.search) : null),
    [isSystemAdmin, location.search]
  );

  const previewKey = adminParams?.get("tourPreview") ?? null;

  /**
   * `?tourPick=<key>`: a system admin choosing the element a new step should point at.
   *
   * While it is on, no tour runs. The overlay makes every other child of `body` inert, so a tour
   * that auto-started here would leave nothing on the page to pick - and the pages worth picking on
   * are exactly the ones with a tour due. Anyone else gets the parameter ignored outright.
   */
  const pickKey = adminParams?.get("tourPick") ?? null;
  const picking = pickKey !== null;

  useEffect(() => {
    if (picking) dispatch({ type: "close" });
  }, [picking]);

  /**
   * Which preview has been dealt with, as `${scope}|${key}`: put on screen, or reported as failed.
   *
   * State rather than a ref, because it decides whether auto-start is held. Until the draft is on
   * screen the provider reports "disabled", exactly as pick mode does; otherwise the page's due
   * published tour starts a frame after the route settles, while the preview is still loading, and
   * the admin watches the published tour instead and has it recorded as seen. Carrying the scope
   * means switching organization runs the preview again there.
   */
  const previewToken = previewKey && scope ? `${scope}|${previewKey}` : null;
  const [settledPreview, setSettledPreview] = useState<string | null>(null);
  const [previewFailure, setPreviewFailure] = useState<{ token: string; message: string } | null>(
    null
  );
  const previewPending = previewToken !== null && settledPreview !== previewToken;
  const previewError =
    previewFailure && previewFailure.token === previewToken ? previewFailure.message : "";

  // The parameter going away forgets the mark, so the same preview link opened again runs again.
  useEffect(() => {
    if (!previewKey) setSettledPreview(null);
  }, [previewKey]);

  const stripPreviewParam = useCallback(() => {
    const params = new URLSearchParams(location.search);
    if (!params.has("tourPreview")) return;
    setPreviewedPath(location.pathname);
    params.delete("tourPreview");
    const search = params.toString();
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : "" },
      { replace: true }
    );
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!previewKey || !previewToken || !previewPending) return;
    // The config for this scope, not a previous one still in state while the new one loads.
    if (state.status !== "ready" || state.scope !== scope) return;
    // Pick mode hides the overlay: a preview started under it would be marked shown unseen.
    if (picking) return;

    const controller = new AbortController();
    getTourPreview(previewKey, "Web", controller.signal)
      .then((previewTour) => {
        if (controller.signal.aborted) return;
        const run = previewTour ? replayRun(previewTour, renderContextRef.current) : null;
        if (run) {
          /*
           * Replaces anything already on screen. This is the one path that takes a run off the
           * screen without ending it, so it owes the page the same tidy-up `end` does — before the
           * replace, while the overlay is still mounted and the reducer still has the replaced
           * run's record of what it opened. Without it the panel that run opened is left inert
           * behind the preview, with nothing left that knows it was the tour that opened it.
           */
          closeWhatTheRunOpened();
          // It counts as previewed once it is seen active (below), not here: a start the reducer
          // refused must leave the preview free to retry.
          dispatch({ type: "start", run: { ...run, preview: true }, replace: true });
          return;
        }
        setPreviewFailure({
          token: previewToken,
          message:
            "That draft has no step this page can show yet. Save a draft with at least one enabled step.",
        });
        setSettledPreview(previewToken);
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        console.warn(`Could not preview tour ${previewKey}:`, error);
        setPreviewFailure({
          token: previewToken,
          message: "That tour draft could not be loaded. Check the tour still exists.",
        });
        setSettledPreview(previewToken);
      });

    return () => controller.abort();
  }, [
    previewKey,
    previewToken,
    previewPending,
    picking,
    scope,
    state.status,
    state.scope,
    closeWhatTheRunOpened,
  ]);

  const activePreviewKey = state.active?.run.preview ? state.active.run.tour.key : null;
  useEffect(() => {
    if (previewToken && state.scope === scope && activePreviewKey === previewKey) {
      setSettledPreview(previewToken);
    }
  }, [activePreviewKey, previewKey, previewToken, scope, state.scope]);

  // The parameter goes when the preview ends, so a reload of the page the admin is standing on
  // does not start the draft over.
  const previewActive = activePreviewKey !== null;
  /** The scope a preview was last on screen in, or null when none has been since it ended. */
  const previewShownIn = useRef<string | null>(null);
  useEffect(() => {
    if (previewActive) {
      previewShownIn.current = state.scope;
      return;
    }
    const shownIn = previewShownIn.current;
    if (shownIn === null) return;
    previewShownIn.current = null;
    // A switch of user or organization closed it rather than the admin ending it: the parameter
    // stays, so the preview runs again in the new scope.
    if (shownIn === state.scope) stripPreviewParam();
  }, [previewActive, state.scope, stripPreviewParam]);

  const record = useCallback(
    (run: TourRun, outcome: TourOutcome, lastStepKey: string | null) => {
      if (run.replay || !guard) return;
      const body: RecordTourProgressRequest = {
        outcome,
        renderedStepKeys: run.steps.map((step) => step.key),
        lastStepKey,
        platform: "Web",
        seenAt: new Date().toISOString(),
      };
      // Stamped with the run's own time, so a later re-show can tell this mark is older than it.
      guard.markDone(run.tour.key, body.renderedStepKeys, body.seenAt);
      guard.addPending(run.tour.key, body);
      void sendProgress(guard, run.tour.key, body);
    },
    [guard]
  );

  /*
   * Finishing, skipping and chaining all come through here, and all three leave the page as the
   * run left it — which, for a tour that ends on a detour, means a popup the tour asked to have
   * opened and nothing to close it with. The page tour then chains the data tour, which inerts the
   * app root, and the popup sits there dimmed and unreachable for the whole of the next run.
   *
   * So the run closes what it opened before it goes, on its way out rather than after the state
   * change.
   */
  const endRun = useCallback(
    (
      outcome: TourOutcome,
      extraSatisfiedKey: string | null = null,
      closeOpened = true,
      mayGoBack = true
    ) => {
      const active = stateRef.current.active;
      if (!active) return;
      cancelRestore();
      // Asked once, while the state still describes this run: what takes the screen after it.
      const follows = runFollowsEnd(stateRef.current, outcome, renderContextRef.current);
      // A board modal the run opened goes only when another run is about to take the screen;
      // otherwise the coach keeps it, and what they typed, once the tour is gone. The refs are
      // synced after each commit, so a queue or page update dispatched in this same task and not yet
      // rendered is not seen; no handler does that before ending a run today.
      if (closeOpened) closeWhatTheRunOpened(extraSatisfiedKey, follows);
      if (outcome === "Finished" && !active.run.preview) {
        finishedHereRef.current = active.run.tour.key;
      }
      record(active.run, outcome, active.run.steps[active.stepIndex]?.key ?? null);
      const continuation = continuationOf(active.run.tour.key);
      // Skipping the first part of a chained walkthrough skips its second part too (the reducer
      // does not start it), so that part is recorded as skipped with it.
      if (outcome === "Skipped" && continuation?.via === "chain") {
        const rest = runAfterEnd(stateRef.current, "Finished", renderContextRef.current);
        if (rest && rest.tour.key === continuation.next) record(rest, "Skipped", null);
      }
      // A Guided tours replay goes back to the list when the walkthrough is over: nothing follows
      // on this page, and this is not the first part of a flow the coach is about to go on with.
      // Not when the run ends on the coach's own press of a control (`anchorUsed`): that press is
      // about to act, and navigating away first would swallow it.
      const returnTo = active.run.returnTo;
      const goesBack =
        mayGoBack &&
        returnTo !== undefined &&
        !follows &&
        !(outcome === "Finished" && continuation?.via === "flow");
      dispatch({ type: "end", outcome, context: renderContext });
      if (goesBack) navigate(returnTo);
    },
    [cancelRestore, closeWhatTheRunOpened, navigate, record, renderContext]
  );

  const finish = useCallback(() => endRun("Finished"), [endRun]);
  const skip = useCallback(() => endRun("Skipped"), [endRun]);

  const next = useCallback(() => {
    const active = stateRef.current.active;
    if (!active) return;
    const { steps } = active.run;
    const leaving = steps[active.stepIndex];
    // A save still landing: the modal cannot be closed and must not be left open behind the next
    // step. The step is armed, so it moves on by itself once the save finishes.
    if (isCommitInFlight(leaving)) return;
    // Past the steps of a popup whose opener is not on the page (`isOrphanedSubStep`).
    const target = shownStepIndex(steps, active.stepIndex);
    noteSkipped(active.run, active.stepIndex, target);
    // A half-finished restore is not what this step looks like: keep the record it already has.
    if (target < steps.length && !cancelRestore()) recordOpenPopups();
    // Next on a commit step (Attach, Save) whose modal is still open skips the save: the modal
    // closes through its own close button, after the record above saw it open, so Back can put it
    // back. Without this the next step's modal would open on top of it.
    closeCommitModal(leaving);
    if (target >= steps.length) {
      finish();
      return;
    }
    // Leaving the last step inside a popup the run opened closes it, so the next step is not
    // pointing at a page hidden behind it.
    closePopupsLeftBehind(steps, active.satisfiedStepKeys, steps[target] ?? null);
    dispatch({ type: "goto", stepIndex: target, rearm: false });
  }, [cancelRestore, finish, noteSkipped, recordOpenPopups]);

  /*
   * Back always lands on the previous step, re-armed, and puts the page back the way that step was
   * shown: the popups the run had open while it was on screen (recorded as the coach left it), no
   * more and no fewer. When the popups open now already match, nothing is pressed. Otherwise what
   * the run opened is closed, and the openers that step needs are pressed again in order, a frame
   * apart, so an opener inside another popup (Customize Tags in the filter modal) can mount first.
   *
   * Re-armed means the landing step's press, and every press after it, no longer count as spent:
   * a step that asked the coach to click Filter asks again, with the filter modal shut, exactly as
   * they first met it. Forward is untouched.
   */
  const back = useCallback(() => {
    const active = stateRef.current.active;
    if (!active || active.stepIndex === 0) return;
    // A save still landing owns the step: leaving it would drop the watch that moves on once the
    // modal closes, and strand the coach on steps about a modal that is gone.
    if (isCommitInFlight(active.run.steps[active.stepIndex])) return;
    cancelRestore();
    const { steps } = active.run;
    let stepIndex = active.stepIndex - 1;
    const skipped = skippedRef.current.run === active.run ? skippedRef.current.keys : null;
    while (stepIndex > 0 && skipped?.has(steps[stepIndex].key)) stepIndex -= 1;
    const target = steps[stepIndex];
    const record =
      openersByStepRef.current.run === active.run ? openersByStepRef.current.byStep : null;
    const desired = record?.get(target.key);

    let plan: string[] = [];
    if (record && desired) {
      const current = openPopupOpenerKeys(steps, active.satisfiedStepKeys, null);
      const same =
        current.length === desired.length && current.every((key) => desired.includes(key));
      if (!same) {
        closeWhatTheRunOpened();
        plan = openerPressPlan(desired, record).flatMap((key) => {
          const anchor = steps.find((candidate) => candidate.key === key)?.anchor;
          return anchor ? [anchor] : [];
        });
      }
    }

    dispatch({ type: "goto", stepIndex, rearm: true });
    if (plan.length > 0) {
      const cancel = pressOpenersInOrder(plan, document, () => {
        if (cancelRestoreRef.current === cancel) cancelRestoreRef.current = null;
      });
      cancelRestoreRef.current = cancel;
    }
  }, [cancelRestore, closeWhatTheRunOpened]);

  /**
   * The coach used the control a `ClickToContinue` step named: record the click as spent, then
   * move on exactly as Next would. The record is what stops the step asking for the same press
   * again when Back and Next bring the coach past it a second time.
   *
   * The last step is the awkward one, and it is reachable: a run made of unseen steps only, or a
   * tour an admin ends on "open Columns", finishes on this press rather than on a Next. Two things
   * about that press are invisible to a tidy-up run in the same handler — the record of it, which
   * only lands on the next render, and the popup it opened, because the tour watches the click in
   * the capture phase and the control's own handler has not run yet. So the end waits a frame, by
   * which time both have landed, and is dropped if anything else ended the run first.
   */
  const anchorUsed = useCallback(() => {
    const active = stateRef.current.active;
    if (!active) return;
    const { run, stepIndex } = active;
    // A half-finished restore is not what this step looks like: keep the record it already has.
    const restoring = cancelRestore();
    const target = shownStepIndex(run.steps, stepIndex);
    noteSkipped(run, stepIndex, target);
    // A commit step (Attach, Save) only gets here once its modal has closed, so its record was taken
    // when the coach pressed the button (`commitPressed`), while the modal was still open.
    const anchor = run.steps[stepIndex]?.anchor;
    const committed = anchor !== null && anchor !== undefined && COACH_PRESS_ANCHORS.has(anchor);
    if (target < run.steps.length && !restoring && !committed) recordOpenPopups();
    dispatch({ type: "anchorUsed" });
    if (target < run.steps.length) {
      // The same check `next` makes. The record of this press has not landed yet, so it is passed
      // in; a popup this press opens is not open yet either, so only an earlier one can close.
      const currentKey = run.steps[stepIndex]?.key;
      closePopupsLeftBehind(
        run.steps,
        currentKey ? [...active.satisfiedStepKeys, currentKey] : active.satisfiedStepKeys,
        run.steps[target] ?? null
      );
      dispatch({ type: "goto", stepIndex: target, rearm: false });
      return;
    }
    const stepKey = run.steps[stepIndex]?.key ?? null;
    // The record is written here, in the same task as the press, because the press may navigate:
    // the control's own handler runs next, and a route change closes the run before any frame of
    // ours arrives. Deferring the write would lose it, and the coach would be shown the tour again.
    const steps = run.steps;
    const satisfied = stepKey === null ? [...active.satisfiedStepKeys] : [...active.satisfiedStepKeys, stepKey];
    // Asked before the end dispatch, while the state still describes this run: a board modal this
    // press opens (or one the run opened earlier) stays with the coach unless another run follows.
    const closeModals = runFollowsEnd(stateRef.current, "Finished", renderContextRef.current);
    // No key to merge: this path closes what the run opened itself, a frame later.
    endRun("Finished", null, false, false);
    // Only the tidy-up waits a frame, because the popup this press opened is not open yet: the tour
    // watches the click in the capture phase, so the control's own handler has not run. It works on
    // plain arrays, so it neither needs the run to still be active nor the overlay to still be up.
    cancelPendingEnd.current?.();
    cancelPendingEnd.current = scheduleFrame(() => {
      cancelPendingEnd.current = null;
      closeAnchorsOpenedByRun(steps, satisfied, document, { closeModals });
    });
  }, [cancelRestore, endRun, noteSkipped, recordOpenPopups]);

  /**
   * The tour pressed an `OpenAnchor` step's control itself: the same record, without moving on.
   * One record for both kinds of press, so "already pressed", "still interactive" and "re-arm
   * on Back" cannot come to three different answers about the same step.
   */
  const anchorOpened = useCallback(() => dispatch({ type: "anchorUsed" }), []);

  /**
   * The coach pressed a commit button (Attach, Save). The step advances only once the modal has
   * closed (`useStepInteraction`), by which time nothing would show it was open, so the record of
   * open popups that Back reads is taken now. A press that fails validation simply takes it again.
   */
  const commitPressed = useCallback(() => {
    if (!cancelRestore()) recordOpenPopups();
  }, [cancelRestore, recordOpenPopups]);

  const getDueRun = useCallback(
    (tourKey: string) => {
      const tour = state.tours.find((candidate) => candidate.key === tourKey);
      const run = tour ? dueRun(tour, findProgress(state.progress, tourKey), renderContext) : null;
      // Nothing due when every step left is about a record the page does not have yet.
      const shown = run ? withoutAbsentSteps(run) : null;
      return shown && shown.steps.length > 0 ? shown : null;
    },
    [state.tours, state.progress, renderContext]
  );

  const startTour = useCallback(
    (tourKey: string, options?: { replay?: boolean }) => {
      const tour = stateRef.current.tours.find((candidate) => candidate.key === tourKey);
      if (!tour) return;
      const run = options?.replay
        ? replayRun(tour, renderContext)
        : dueRun(tour, findProgress(stateRef.current.progress, tourKey), renderContext);
      if (run) dispatch({ type: "start", run: withoutAbsentSteps(run) });
    },
    [renderContext]
  );

  const getReplayRun = useCallback(
    (featureKey: string, keys?: TourKeyOverrides): TourRun | null => {
      const { tours, pages } = stateRef.current;
      // By key rather than by kind: a feature can carry more than one Page tour (schedule has its
      // catalog view), and "the first Page tour" would be whichever the API listed first.
      const resolved = resolveTourKeys(featureKey, keys);
      const byKey = (key: string | null) =>
        key === null ? undefined : tours.find((candidate) => candidate.key === key);
      const pageTour = byKey(resolved.page);
      const dataTour = pages[featureKey]?.hasData ? byKey(resolved.data) : undefined;
      return (
        (pageTour && replayRun(pageTour, renderContext)) ||
        (dataTour && replayRun(dataTour, renderContext)) ||
        null
      );
    },
    [renderContext]
  );

  const replayFeature = useCallback(
    (featureKey: string, keys?: TourKeyOverrides) => {
      const run = getReplayRun(featureKey, keys);
      if (run) dispatch({ type: "start", run: withoutAbsentSteps(run) });
    },
    [getReplayRun]
  );

  const requestReplay = useCallback(
    (
      featureKey: string,
      keys: TourKeyOverrides | undefined,
      path: string,
      options?: { record?: boolean; returnTo?: string }
    ) => {
      // Always a new object, so choosing the same tour twice runs it twice.
      setPendingReplay({
        featureKey,
        pageKey: resolveTourKeys(featureKey, keys).page,
        path,
        standalone: keys?.dataTourKey === null,
        ...(options?.record ? { record: true } : {}),
        ...(options?.returnTo ? { returnTo: options.returnTo } : {}),
      });
    },
    []
  );

  const finishedHere = useCallback(
    (tourKey: string) => finishedHereRef.current === tourKey,
    []
  );

  const handOff = useCallback(
    (fromTourKey: string, to: TourHandOff): boolean => {
      const active = stateRef.current.active;
      const activeKey = active?.run.tour.key ?? null;
      if (activeKey !== fromTourKey && !finishedHere(fromTourKey)) return false;
      finishedHereRef.current = null;
      // A tour still due (never seen, or with steps unseen) is requested in full as a recorded run,
      // so the coach gets every step and the run counts, exactly as a first auto-start would. A plain
      // replay would record nothing and the same tour would come back on the next visit. The new
      // roster's tour never auto-starts on its own (`useNewRosterTour`), so it has to be asked for
      // either way. Only a tour already seen through gets the plain replay.
      const { tours, progress } = stateRef.current;
      const nextKey = resolveTourKeys(to.featureKey, to.keys).page;
      const nextTour = tours.find((candidate) => candidate.key === nextKey);
      const due = nextTour
        ? dueRun(nextTour, findProgress(progress, nextKey), renderContextRef.current)
        : null;
      // A Guided tours replay of the first tour goes back to the list after this one. Finished
      // first, the reducer's carry-on holds the way back; handed off mid tour (the coach made the
      // roster while the list tour was still up), the run is closed by the navigation, so it is
      // taken from the run itself.
      const carriesReturn = continuationOf(fromTourKey)?.carriesReturn === true;
      const returnTo = carriesReturn
        ? activeKey === fromTourKey
          ? active?.run.returnTo
          : stateRef.current.carryOn?.tourKey === nextKey
            ? stateRef.current.carryOn.returnTo
            : undefined
        : undefined;
      const options = {
        ...(due ? { record: true } : {}),
        ...(returnTo ? { returnTo } : {}),
      };
      requestReplay(to.featureKey, to.keys, to.path, Object.keys(options).length > 0 ? options : undefined);
      return true;
    },
    [finishedHere, requestReplay]
  );

  const clearReplay = useCallback((request: ReplayRequest) => {
    setPendingReplay((current) => (current === request ? null : current));
  }, []);

  const hasTours = useCallback(
    (featureKey: string, keys?: TourKeyOverrides) => {
      if (picking || state.status !== "ready") return false;
      // By key, the same tours `replayFeature` would run, so the Guided tours list never offers a
      // replay that does nothing (schedule's page tour while only the catalog tour can render).
      const resolved = resolveTourKeys(featureKey, keys);
      return state.tours.some(
        (tour) =>
          (tour.key === resolved.page || tour.key === resolved.data) &&
          renderableSteps(tour, renderContext).length > 0
      );
    },
    [picking, state.status, state.tours, renderContext]
  );

  const hasPageTour = useCallback(
    (featureKey: string, keys?: TourKeyOverrides) => {
      if (picking || state.status !== "ready") return false;
      const pageKey = resolveTourKeys(featureKey, keys).page;
      const tour = state.tours.find((candidate) => candidate.key === pageKey);
      return tour !== undefined && renderableSteps(tour, renderContext).length > 0;
    },
    [picking, state.status, state.tours, renderContext]
  );

  // Asked again as the run starts: auto-start waits a frame after `getDueRun`, and a replay asks
  // `getReplayRun`, which does not look at the page.
  const startRun = useCallback(
    (run: TourRun) => dispatch({ type: "start", run: withoutAbsentSteps(run) }),
    []
  );
  const queueRun = useCallback((run: TourRun) => dispatch({ type: "queue", run }), []);
  const updatePage = useCallback(
    (featureKey: string, registration: PageRegistration) =>
      dispatch({ type: "updatePage", featureKey, registration }),
    []
  );
  const unregisterPage = useCallback(
    (featureKey: string) => dispatch({ type: "unregisterPage", featureKey }),
    []
  );

  const value = useMemo<TourContextValue>(
    () => ({
      // "disabled" while picking, while a preview is loading, on screen or reporting a failure, and
      // for the rest of the visit to the page it ran on, is what holds auto-start back: every gate
      // in `useTour` reads it.
      status: picking || previewKey !== null || previewVisit ? "disabled" : state.status,
      whenRouteSettled,
      pathname: location.pathname,
      activeTour: state.active,
      hasTours,
      hasPageTour,
      getDueRun,
      updatePage,
      unregisterPage,
      startTour,
      startRun,
      queueRun,
      replayFeature,
      getReplayRun,
      pendingReplay,
      requestReplay,
      handOff,
      finishedHere,
      clearReplay,
      next,
      back,
      skip,
      finish,
    }),
    [
      picking,
      previewKey,
      previewVisit,
      state.status,
      state.active,
      whenRouteSettled,
      location.pathname,
      hasTours,
      hasPageTour,
      getDueRun,
      updatePage,
      unregisterPage,
      startTour,
      startRun,
      queueRun,
      replayFeature,
      getReplayRun,
      pendingReplay,
      requestReplay,
      handOff,
      finishedHere,
      clearReplay,
      next,
      back,
      skip,
      finish,
    ]
  );

  const active = state.active;
  // A data tour whose page lost its data points at rows that are gone: show centered cards.
  const suppressAnchor = active !== null && anchorSuppressed(state, active.run);

  /*
   * The next part of a numbered walkthrough (`TOUR_CONTINUATIONS`), while it is going to follow:
   * its step count goes on the counter, and the last step here says Next instead of Finish. A
   * chained data tour follows only when the provider would chain it now (the page has data, the
   * tour is due or this is a replay). A flow's next tour plays once the coach goes on, as a due run
   * or a replay, so it is counted as one of those.
   */
  const stepsToFollow = useMemo(() => {
    if (!active || active.run.preview) return 0;
    const continuation = continuationOf(active.run.tour.key);
    if (!continuation?.numbered) return 0;
    if (continuation.via === "chain") {
      const following = runAfterEnd(state, "Finished", renderContext);
      return following?.tour.key === continuation.next ? following.steps.length : 0;
    }
    const nextTour = state.tours.find((candidate) => candidate.key === continuation.next);
    if (!nextTour) return 0;
    const nextRun =
      dueRun(nextTour, findProgress(state.progress, nextTour.key), renderContext) ??
      replayRun(nextTour, renderContext);
    return nextRun?.steps.length ?? 0;
  }, [active, state, renderContext]);

  return (
    <TourContext.Provider value={value}>
      {children}
      {previewError && (
        <TourPreviewNotice
          message={previewError}
          onDismiss={() => {
            setPreviewFailure(null);
            stripPreviewParam();
          }}
        />
      )}
      {pickKey && <TourPickMode tourKey={pickKey} />}
      {active && !picking && (
        <TourOverlay
          run={active.run}
          stepIndex={active.stepIndex}
          stepsToFollow={stepsToFollow}
          suppressAnchor={suppressAnchor}
          satisfiedStepKeys={active.satisfiedStepKeys}
          onNext={next}
          onBack={back}
          onSkip={skip}
          onAnchorUsed={anchorUsed}
          onAnchorOpened={anchorOpened}
          onCommitPressed={commitPressed}
        />
      )}
    </TourContext.Provider>
  );
};
